import type { Styles } from '@adornis/ass/style.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { ComponentSizeController } from '@adornis/chemistry/controllers/component-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { LivekitController, type LivekitControllerRemoteElement } from '@adornis/web-rtc/client/livekitController.js';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement } from 'lit/decorators.js';
import { property } from 'lit/decorators/property.js';
import { state } from 'lit/decorators/state.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  type LocalTrack,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  Track,
} from 'livekit-client';
import { getLivekitAccessToken } from '../../../db/livekit.js';

@customElement('ils-video-chat')
export class ILSVideoChat extends ChemistryLitElement {
  @property({ attribute: 'room-id' }) roomIDAttribute = '';
  // if you are visible on the remotes
  @property({ type: Boolean }) incognito = false;
  @property({ type: Boolean }) disableMicrophone = false;
  @property({ type: Boolean }) disableCamera = false;
  protected roomID = '';

  protected readonly textEncoder = new TextEncoder();
  protected readonly textDecoder = new TextDecoder();
  protected readonly ownVideoElement = createRef<HTMLMediaElement>();
  protected readonly ownScreenShareElement = createRef<HTMLVideoElement>();
  protected readonly size = new ComponentSizeController(this, { debounceTime: 200 });

  protected readonly VideoBoxElementClass = ILSVideoChatBox;

  livekitController = new LivekitController(this, {
    ownCameraVideoRef: this.ownVideoElement,
    createLocalTracks: async participant => {
      const currentTracks = participant.getTrackPublications().map(pub => pub.track as LocalTrack);
      const tracks = currentTracks.length
        ? currentTracks
        : await participant.createTracks({
            audio: true,
            video: {
              // @ts-expect-error we only want to set the framerate and dont care about the resolution
              resolution: {
                frameRate: 24,
              },
            },
          });
      for (const track of tracks) {
        if (
          this.incognito ||
          (track.source === Track.Source.Microphone && this.disableMicrophone) ||
          (track.source === Track.Source.Camera && this.disableCamera)
        )
          track.mute();
        else track.unmute();
      }
      return tracks;
    },
    createRemoteParticipantElement: participant => {
      const element = new this.VideoBoxElementClass();
      element.participant = participant;
      return element;
    },
    onDataReceived: (payload, participant) => this.ondata(payload, participant),
    onTrackSubscribed: (track, publication, participant) => this.ontrack(track, publication, participant),
  });

  // willUpdate is used to auto join the roomID set via attribute
  protected override async willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): Promise<void> {
    if (
      _changedProperties.has('roomIDAttribute') &&
      this.roomIDAttribute &&
      _changedProperties.get('roomIDAttribute') !== this.roomID
    ) {
      await this._joinRoom(this.roomIDAttribute);
    }
  }

  sendData(data: string, destinationIdentities?: string | string[]) {
    this.livekitController.sendData(this.textEncoder.encode(data), destinationIdentities);
  }

  async toggleMicrophone() {
    return this.livekitController.toggleMicrophone();
  }
  async toggleCamera() {
    return this.livekitController.toggleCamera();
  }
  async setMicrophone(enabled: boolean) {
    this.disableMicrophone = !enabled;
    await this.livekitController.setMicrophone(enabled);
  }
  async setCamera(enabled: boolean) {
    this.disableCamera = !enabled;
    await this.livekitController.setCamera(enabled);
  }
  getInvisible() {
    const metadata = this.livekitController.getLocalParticipantMetadata();
    if (!metadata) return false;
    try {
      return !!JSON.parse(metadata).invisible;
    } catch (error: any) {
      return false;
    }
  }
  setInvisible(enabled: boolean) {
    this.incognito = enabled;
    this.livekitController.setLocalParticipantMetadata(JSON.stringify({ invisible: enabled }));
  }
  toggleInvisible() {
    this.setInvisible(!this.getInvisible());
  }

  protected async _joinRoom(roomID = this.roomID) {
    if (!roomID) throw new Error('cannot joinRoom without roomID!');
    this.roomID = roomID;
    const token = await getLivekitAccessToken(roomID, this.incognito)();
    // const token = await getLivekitRoomToken(this.currentSlot.roomID)();
    await this.livekitController.joinRoom(token);
  }

  protected ontrack(track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) {}

  protected ondata(data: Uint8Array, participant?: RemoteParticipant) {
    this.dispatchEvent(
      new CustomEvent('data', {
        detail: { member: participant?.identity ?? 'server', value: this.textDecoder.decode(data) },
      }),
    );
  }

  remoteVideo(element: LivekitControllerRemoteElement): Renderable {
    return element;
  }

  override render() {
    const remoteMedia = [...this.livekitController.remoteMedia()];
    const memberCount = remoteMedia.filter(
      ([participant, boxElement]) => !(boxElement as ILSVideoChatBox).isInvisible(),
    ).length;
    const { columns, elementSize, rows } = this.computeGridParams(memberCount);
    const localErrors = this.livekitController.getLocalErrors();
    const localState = this.livekitController.getLocalTrackState();
    return html`
      <x-grid
        columns="repeat(${columns}, ${elementSize})"
        rows="repeat(${rows}, ${elementSize})"
        space="md"
        ${css({ width: '100%', height: '100%', position: 'relative' })}
      >
        ${localErrors.size || memberCount === 0
          ? html`
              <x-flex ${css({ position: 'absolute', top: '0', left: '0', bottom: '0', right: '0' })}>
                ${localErrors.size ? this.retryStream() : this.emptyRoom()}
              </x-flex>
            `
          : repeat(remoteMedia, ([member, boxElement]) => this.remoteVideo(boxElement))}
        <video
          ${css({
            display: localState.camera ? 'block' : 'none',
            position: 'absolute',
            borderRadius: '5px',
            objectFit: 'cover',
            bottom: '1em',
            right: '1em',
            width: '20vmin',
          })}
          ${ref(this.ownVideoElement)}
          autoplay
          muted
        ></video>
        <video
          ${css({
            display: /*this.rtcController.memberInfo.screenshare ? 'block' : */ 'none',
            position: 'absolute',
            borderRadius: '5px',
            bottom: '1em',
            right: 'calc(2em + 10vmin)',
            width: '10vmin',
          })}
          ${ref(this.ownScreenShareElement)}
          autoplay
          muted
        ></video>
        ${this.statusDisplay()}
      </x-grid>
    `;
  }

  statusDisplay() {
    if (!this.livekitController.isConnected())
      return html`
        <x-basic-card padding="md" ${css({ position: 'absolute', bottom: '1em', left: '1em' })}>
          <x-text> Warte auf Raum... </x-text>
        </x-basic-card>
      `;
    const errors = this.livekitController.getLocalErrors();
    if (errors.size)
      return html`
        <x-basic-card padding="md" ${css({ position: 'absolute', bottom: '1em', left: '1em' })}>
          <x-text> Kein lokaler Stream erstellt. </x-text>
        </x-basic-card>
      `;
    return this.statusDisplayWhenReady();
  }

  statusDisplayWhenReady() {}

  emptyRoom() {
    return html`
      <x-flex flex center crossaxis-center space="md">
        <x-h2> Dieser Raum ist leer </x-h2>
        <x-text> Ihr Gegenüber sollte zeitnah, spätestens nach Ende der Pause, dazustoßen. </x-text>
        <x-button
          mode="outline"
          icon="router"
          @click=${() =>
            // ! do we need this?
            window.location.reload()}
          >Neu verbinden</x-button
        >
      </x-flex>
    `;
  }

  retryStream() {
    const errors = this.livekitController.getLocalErrors();
    return html`
      <x-flex flex center crossaxis-center space="md">
        <x-h2> Der Videofeed konnte nicht gestartet werden. </x-h2>
        <x-text> Laufen möglicherweise andere Apps, welche die Kamera zur Zeit verwenden? </x-text>
        <x-text> Haben Sie der Webseite die notwendigen Rechte erteilt um auf Ihre Kamera zuzugreifen? </x-text>
        <x-button mode="outline" @click=${() => this._joinRoom()}> Erneut versuchen </x-button>
        <div></div>
        <x-h3> Fehler Beschreibung </x-h3>
        ${[...errors].map(
          error => html`<pre ${css({ marginTop: '0' })}> ${error.stack ? error.stack : error.message} </pre>`,
        )}
      </x-flex>
    `;
  }

  protected computeGridParams(memberCount: number) {
    let elementSize: string;
    let columns: number;
    let rows: number;
    if (typeof this.size.width === 'undefined' || typeof this.size.height === 'undefined')
      throw new Error('cannot layout without size info!');
    if (memberCount > 1) {
      const gap = this.spacing.md.number;
      const columnsW = Math.ceil(Math.sqrt((memberCount * this.size.width) / this.size.height));
      const rowsW = Math.ceil(memberCount / columnsW);
      const elementSizeW = Math.min(
        (this.size.width - gap * (columnsW - 1)) / columnsW,
        (this.size.height - gap * (rowsW - 1)) / rowsW,
      );

      const rowsH = Math.ceil(Math.sqrt((memberCount * this.size.height) / this.size.width));
      const columnsH = Math.ceil(memberCount / rowsH);
      const elementSizeH = Math.min(
        (this.size.width - gap * (columnsH - 1)) / columnsH,
        (this.size.height - gap * (rowsH - 1)) / rowsH,
      );

      if (elementSizeW > elementSizeH) {
        columns = columnsW;
        rows = rowsW;
        elementSize = elementSizeW + 'px';
      } else {
        columns = columnsH;
        rows = rowsH;
        elementSize = elementSizeH + 'px';
      }
    } else {
      columns = 1;
      rows = 1;
      elementSize = '1fr';
    }
    return { columns, elementSize, rows };
  }
}

@customElement('ils-video-chat-box')
export class ILSVideoChatBox extends ChemistryLitElement {
  @state() participant?: RemoteParticipant;
  @property({ attribute: true, type: Boolean }) loading = false;
  @state() private _cameraElement?: HTMLMediaElement;
  @state() private _audioElement?: HTMLMediaElement;

  override connectedCallback(): void {
    super.connectedCallback();
    if (!this.participant) throw new Error('ils-video-chat-box requires participant before attaching it into the DOM');
  }

  trackSubscribed(track: RemoteTrack, publication: RemoteTrackPublication) {
    if (publication.source === Track.Source.Camera) {
      this._cameraElement = track.attach();
      this._cameraElement.setAttribute('camera', '');
    }
    if (publication.source === Track.Source.Microphone) {
      this._audioElement = track.attach();
      this._audioElement.setAttribute('microphone', '');
    }
  }

  override render() {
    if (!this.participant) return nothing;
    return html`
      <x-flex horizontal ${css({ width: '100%', height: '100%', opacity: this._cameraElement ? '1' : '0' })}>
        ${this._cameraElement} ${this._audioElement}
      </x-flex>
      ${this.stateOverlays()} ${this.loadingOverlay(this.loading)}
    `;
  }

  protected stateOverlays(): Renderable {
    if (!this.participant) return nothing;
    const microphoneEnabled = this.participant.getTrackPublication(Track.Source.Microphone)?.isMuted === false;
    const cameraEnabled = this.participant.getTrackPublication(Track.Source.Camera)?.isMuted === false;
    return html`
      ${microphoneEnabled && !cameraEnabled ? this.audioOnly() : nothing}
      ${cameraEnabled && !microphoneEnabled ? this.mutedOverlay() : nothing}
      ${!cameraEnabled && !microphoneEnabled ? this.noTracksOverlay() : nothing}
    `;
  }

  protected loadingOverlay(visible) {
    return html`
      <x-flex
        ${css({
          transition: 'backdropFilter 150ms',
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          background: 'none',
          backdropFilter: visible ? 'blur(5px)' : '',
        })}
        horizontal
        center
        crossaxis-center
      >
        ${visible ? html` <x-loader></x-loader> ` : nothing}
      </x-flex>
    `;
  }

  protected audioOnly(): Renderable {
    return html`
      <x-flex
        ${css({ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', fontSize: '3em' })}
        center
        crossaxis-center
      >
        <x-icon> call </x-icon>
      </x-flex>
    `;
  }

  protected mutedOverlay() {
    return html`
      <x-basic-card
        padding="md"
        ${css({
          position: 'absolute',
          top: '1em',
          right: '1em',
          fontSize: '3em',
        })}
      >
        <x-icon style="color: darkred;"> mic_off </x-icon>
      </x-basic-card>
    `;
  }

  protected noTracksOverlay(): Renderable {
    return html`
      <x-flex
        ${css({ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', fontSize: '3em' })}
        horizontal
        center
        crossaxis-center
      >
        <x-icon style="color: darkred;"> mic_off </x-icon>
        <x-icon style="color: darkred;"> videocam_off </x-icon>
      </x-flex>
    `;
  }

  isInvisible() {
    try {
      const metadata = JSON.parse(this.participant?.metadata ?? '{}');
      return metadata.invisible;
    } catch (error: any) {
      return false;
    }
  }

  override styles() {
    return [
      {
        ':host': {
          display: this.isInvisible() ? 'none' : 'block',
          position: 'relative',
          height: '100%',
          width: '100%',
          minHeight: '0',
          borderRadius: this.sizes.borderRadius,
          overflow: 'hidden',
        },
        'video[camera]': this.mainVideoCSS(),
        'video[screen]': this.screenshareVideoCSS(),
      },
    ];
  }

  protected mainVideoCSS(): Styles {
    // return this.memberInfo?.screenshare
    //   ? {
    //       position: 'absolute',
    //       top: '1em',
    //       right: '1em',
    //       borderRadius: '5px',
    //       width: '10%',
    //       aspectRatio: '1 / 1',
    //       objectFit: 'cover',
    //     }
    //   :
    return {
      borderRadius: '5px',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    };
  }

  protected screenshareVideoCSS(): Styles {
    // return this.memberInfo?.screenshare
    //   ? {
    //       borderRadius: '5px',
    //       width: '100%',
    //       height: '100%',
    //     }
    //   :
    return {
      display: 'none',
    };
  }
}
